import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, DataTable, Toast, ConfirmModal } from '../../../shared/components';
import { Edit, Upload, Download, Plus, Trash2, History } from 'lucide-react';
import { useStudentsManagement, useConfirm } from '../../../shared/hooks';

import StudentsFilters from './StudentsFilters';
import StudentFormModal from './StudentFormModal';
import StudentImportModal from './StudentImportModal';
import { useRoles } from '../../../shared/hooks';

/**
 * Lista de Alumnos con filtros avanzados
 * Permite filtrar por nombre, evento, estado y código de acceso
 */
const StudentsList = () => {
  const {
    students,
    loading,
    error,
    filters,
    pagination,
    fetchStudents,
    createStudent,
    updateStudent,
    deleteStudent,
    applyStudentFilters,
    applyFiltersNow,
    clearFilters,
    exportStudents,
    importStudentsFromExcel,
    changePage,
  } = useStudentsManagement();

  // Hook para confirmaciones
  const { confirmState, confirm, closeConfirm } = useConfirm();

  const { isAdmin } = useRoles();
  const navigate = useNavigate();

  // Estados de modales
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Mapa de eventos por id para mostrar título - tiempo
  const [eventsMap, setEventsMap] = useState({});

  // Estados de notificaciones
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'success'
  });

  // Cargar Alumnos al montar el componente
  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]); // Incluir fetchStudents como dependencia

  // Efecto separado para cambios de paginación
  useEffect(() => {
    if (pagination.page > 1) { // Evitar llamada inicial
      fetchStudents();
    }
  }, [pagination.page, fetchStudents]); // Incluir fetchStudents como dependencia

  // Recibir eventos desde StudentsFilters vía callback (memoizado para evitar loops)
  const handleEventsLoaded = useCallback((events) => {
    const map = (events || []).reduce((acc, ev) => {
      acc[ev.id] = ev;
      return acc;
    }, {});
    setEventsMap(map);
  }, []);

  // Mostrar notificación
  const showToast = (message, type = 'success') => {
    setToast({
      isVisible: true,
      message,
      type
    });
  };

  // Ocultar notificación
  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  // Manejar cambio de página
  const handlePageChange = useCallback((newPage) => {
    changePage(newPage);
    // El useEffect se encargará de recargar los estudiantes
  }, [changePage]);

  // Manejar búsqueda y filtros
  const handleSearch = async (newFilters = null) => {
    if (newFilters) {
      // Si se pasan filtros específicos, usarlos directamente
      await applyFiltersNow(newFilters);
    } else {
      // Si no se pasan filtros, usar los del estado
      await applyFiltersNow();
    }
  };

  // Manejar cambio de filtros (solo actualizar estado, no aplicar)
  const handleFiltersChange = (newFilters) => {
    applyStudentFilters(newFilters);
  };

  // Manejar limpieza de filtros
  const handleClearFilters = () => {
    clearFilters();
    fetchStudents();
  };

  // Manejar edición de Alumno
  const handleEditStudent = (student) => {
    setSelectedStudent(student);
    setIsEditModalOpen(true);
  };

  // Manejar eliminación de Alumno
  const handleDeleteStudent = async (student) => {
    const confirmed = await confirm({
      title: 'Confirmar Eliminación',
      message: `¿Estás seguro de que quieres eliminar al alumno "${student.name}"? Esta acción no se puede deshacer.`,
      type: 'danger',
      confirmText: 'Eliminar Alumno',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        try {
          await deleteStudent(student.id);
          showToast('Alumno eliminado exitosamente');
        } catch (error) {
          showToast(error.message || 'Error al eliminar el alumno', 'error');
        }
      }
    });
    
    if (!confirmed) {
      showToast('Operación cancelada', 'warning');
    }
  };

  // Manejar apertura de modal de creación
  const handleCreateStudent = () => {
    setSelectedStudent(null);
    setIsCreateModalOpen(true);
  };

  // Manejar creación de Alumno
  const handleCreateStudentSubmit = async (studentData) => {
    try {
      await createStudent(studentData);
      setIsCreateModalOpen(false);
      showToast('Alumno creado exitosamente');
    } catch (error) {
      showToast(error.message, 'error');
      throw error;
    }
  };

  // Manejar actualización de Alumno
  const handleUpdateStudent = async (studentData) => {
    try {
      await updateStudent(selectedStudent.id, studentData);
      setIsEditModalOpen(false);
      setSelectedStudent(null);
      showToast('Alumno actualizado exitosamente');
    } catch (error) {
      showToast(error.message, 'error');
      throw error;
    }
  };

  // Manejar importación de Excel
  const handleImportStudents = async (formData) => {
    try {
      const result = await importStudentsFromExcel(formData);
      setIsImportModalOpen(false);
      showToast(`Importación exitosa: ${result.imported || 0} Alumnos importados`);
    } catch (error) {
      showToast(error.message, 'error');
      throw error;
    }
  };

  // Manejar exportación
  const handleExport = async () => {
    try {
      await exportStudents(filters);
      showToast('Alumnos exportados exitosamente');
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  // Formatear fecha
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // Determinar estado basado en reservas
  const getStudentStatus = (student) => {
    if (!student.reservations || student.reservations.length === 0) {
      return { text: 'Sin reservas', color: 'bg-gray-100 text-gray-800' };
    }

    // Filtrar solo las reservas activas (excluir canceladas y devueltas)
    const activeReservations = student.reservations.filter(r => 
      r.status !== 'cancelled' && r.status !== 'returned'
    );

    if (activeReservations.length === 0) {
      return { text: 'Sin reservas', color: 'bg-gray-100 text-gray-800' };
    }

    // Determinar estados presentes (orden de menor a mayor prioridad)
    const hasReserved = activeReservations.some(r => 
      r.status === 'confirmed' || r.status === 'pending' || r.status === 'blocked'
    );
    const hasPaid = activeReservations.some(r => r.status === 'sold');
    const hasDelivered = activeReservations.some(r => 
      r.status === 'delivered' || r.status === 'completed'
    );

    // Si tiene reservas en estado "reservado" (menor prioridad), mostrar ese estado
    if (hasReserved) {
      return { text: 'Reservado', color: 'bg-yellow-100 text-yellow-800' };
    }

    // Si tiene reservas pagadas pero ninguna reservada, mostrar pagado
    if (hasPaid) {
      return { text: 'Pagado', color: 'bg-blue-100 text-blue-800' };
    }

    // Si solo tiene reservas entregadas, mostrar entregado
    if (hasDelivered) {
      return { text: 'Entregado', color: 'bg-green-100 text-green-800' };
    }

    // Si tiene reservas pero ninguna está en estado activo reconocido
    return { text: 'Sin reservas', color: 'bg-gray-100 text-gray-800' };
  };

  // Configuración de columnas de la tabla (Nombre, Teléfono, Evento, Código de acceso, Estado, Reservas, Fecha de registro, Acciones)
  const columns = [
    {
      key: 'name',
      label: 'Nombre',
      render: (student) => (
        <div className="font-medium text-gray-900">
          {student.name}
        </div>
      )
    },

    {
      key: 'phone',
      label: 'Teléfono',
      render: (student) => (
        <div className="text-gray-600">
          {student.phone || 'No registrado'}
        </div>
      )
    },
    {
      key: 'event',
      label: 'Evento',
      render: (student) => {
        const eventId = student?.accessCodes?.[0]?.eventId || student?.reservations?.[0]?.eventId;
        const event = eventId ? eventsMap[eventId] : null;
        const title = event?.title || eventId || '—';
        const timeRaw = event?.eventTime || event?.time || '';
        const time = timeRaw ? ` - ${String(timeRaw).slice(0,5)}` : '';
        return (
          <div className="text-gray-700">
            {title}{time}
          </div>
        );
      }
    },
    {
      key: 'accessCodes',
      label: 'Código de acceso',
      render: (student) => (
        <div className="text-sm">
          {student.accessCodes && student.accessCodes.length > 0 ? (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {student.accessCodes[0].code}
            </span>
          ) : (
            <span className="text-gray-400">Sin códigos</span>
          )}
        </div>
      )
    },
    {
      key: 'status',
      label: 'Estado',
      render: (student) => {
        const status = getStudentStatus(student);
        return (
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
            {status.text}
          </span>
        );
      }
    },
    {
      key: 'reservations',
      label: 'Reservas',
      render: (student) => {
        // Contar solo las reservas activas (excluir canceladas y devueltas)
        const activeReservationsCount = student.reservations 
          ? student.reservations.filter(r => r.status !== 'cancelled' && r.status !== 'returned').length 
          : 0;
        
        return (
          <div className="text-sm">
            {activeReservationsCount > 0 ? (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {activeReservationsCount} reserva{activeReservationsCount !== 1 ? 's' : ''}
              </span>
            ) : (
              <span className="text-gray-400">Sin reservas</span>
            )}
          </div>
        );
      }
    },
    {
      key: 'createdAt',
      label: 'Fecha de Registro',
      render: (student) => (
        <div className="text-sm text-gray-600">
          {formatDate(student.createdAt)}
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (student) => (
        <div className="flex space-x-2">
          {isAdmin && (
            <Button
              onClick={() =>
                navigate(
                  `/admin/audit-logs?studentId=${student.id}&studentName=${encodeURIComponent(student.name || '')}`
                )
              }
              variant="ghost"
              size="sm"
              className="text-slate-600 hover:text-slate-800 hover:bg-slate-50"
              title="Ver historial de movimientos"
            >
              <History className="w-4 h-4" />
            </Button>
          )}

          <Button
            onClick={() => handleEditStudent(student)}
            variant="ghost"
            size="sm"
            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
            title="Editar alumno"
          >
            <Edit className="w-4 h-4" />
          </Button>

          <Button
            onClick={() => handleDeleteStudent(student)}
            variant="ghost"
            size="sm"
            className="text-red-600 hover:text-red-800 hover:bg-red-50"
            title="Eliminar alumno"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )
    }
  ];

  return (
      <div className="space-y-6">
        {/* Header de la sección */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Gestión de Alumnos
            </h1>
            <p className="text-gray-600 mt-1">
              Administra Alumnos, importa desde Excel y gestiona códigos de acceso
            </p>
          </div>
          
          {/* Botones de acción */}
          <div className="flex space-x-3">
            <Button
              onClick={handleCreateStudent}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Alumno
            </Button>
            {isAdmin && (
              <Button
                onClick={() => setIsImportModalOpen(true)}
                variant="outline"
                disabled={loading}
                className="text-green-600 hover:text-green-700 border-green-300 hover:border-green-400"
              >
                <Upload className="w-4 h-4 mr-2" />
                Importar Excel
              </Button>
            )}
            <Button
              onClick={handleExport}
              variant="outline"
              disabled={loading || students.length === 0}
              className="text-orange-600 hover:text-orange-700 border-orange-300 hover:border-orange-400"
            >
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </Button>
          </div>
        </div>

        {/* Filtros */}
        <StudentsFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onClearFilters={handleClearFilters}
          onSearch={handleSearch}
          loading={loading}
          onEventsLoaded={handleEventsLoaded}
        />

        {/* Tabla de Alumnos */}
        <div className="bg-white rounded-lg shadow">
          <DataTable
            section="Alumnos"
            data={students}
            columns={columns}
            loading={loading}
            error={error}
            onRetry={fetchStudents}
            emptyMessage="No se encontraron alumnos"
            showSearch={false}
            showFilters={false}
            pagination={pagination}
            onPageChange={handlePageChange}
          />
        </div>

        {/* Modal de creación */}
        <StudentFormModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={handleCreateStudentSubmit}
          title="Crear Nuevo Alumno"
          submitText="Crear Alumno"
        />

        {/* Modal de edición */}
        <StudentFormModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedStudent(null);
          }}
          onSubmit={handleUpdateStudent}
          student={selectedStudent}
          title="Editar Alumno"
          submitText="Actualizar Alumno"
        />

        {/* Modal de importación */}
        <StudentImportModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          onImport={handleImportStudents}
        />

        {/* Modal de confirmación */}
        <ConfirmModal
          isOpen={confirmState.isOpen}
          title={confirmState.title}
          message={confirmState.message}
          confirmText={confirmState.confirmText}
          cancelText={confirmState.cancelText}
          type={confirmState.type}
          onConfirm={confirmState.onConfirm}
          onClose={closeConfirm}
          onCancel={closeConfirm}
        />

        {/* Notificaciones */}
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={hideToast}
          duration={4000}
        />
      </div>
  );
};

export default StudentsList;